import { useMutation, useQuery } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

/**
 * Hooks for `backend/app_core/routers/tiktok_admin.py` — the admin-only
 * TikTok OAuth selection flow (T3.12).
 *
 * Deviation note (mirrors `frontend/src/api/hooks/sync.ts`): the
 * `/select-accounts` and `/connect-accounts` FastAPI handlers return
 * manually-built dict payloads with no `response_model`, so the generated
 * OpenAPI types for the body are effectively `unknown`. We therefore
 * hand-write the response shape here.
 *
 * TODO: replace with `paths["/api/admin/tiktok/select-accounts"]["get"]`
 * once the endpoint has a response_model.
 */

export interface SelectableAccount {
  advertiser_id: string
  advertiser_name: string
  already_connected: boolean
}

export interface SelectAccountsResponse {
  accounts: SelectableAccount[]
  session_token: string
}

export interface ConnectAccountsResponse {
  success: boolean
  message?: string
  saved?: number
  updated?: number
}

export const TIKTOK_ADMIN_QUERY_KEYS = {
  all: ["tiktokAdmin"] as const,
  selectAccounts: (session: string | null) =>
    ["tiktokAdmin", "select-accounts", session] as const,
} as const

export function useSelectTikTokAccounts(session: string | null) {
  return useQuery<SelectAccountsResponse>({
    queryKey: TIKTOK_ADMIN_QUERY_KEYS.selectAccounts(session),
    queryFn: () =>
      apiFetch<SelectAccountsResponse>(
        `/api/admin/tiktok/select-accounts?session=${encodeURIComponent(
          session ?? "",
        )}`,
      ),
    enabled: session !== null && session !== "",
    retry: false,
    staleTime: 0,
    gcTime: 0,
  })
}

interface ConnectAccountsArgs {
  session_token: string
  selected_account_ids: string[]
}

export function useConnectSelectedTikTokAccounts() {
  return useMutation<ConnectAccountsResponse, Error, ConnectAccountsArgs>({
    mutationFn: (body) =>
      apiFetch<ConnectAccountsResponse>("/api/admin/tiktok/connect-accounts", {
        method: "POST",
        body,
      }),
  })
}
